import React from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormHelperText,
  Chip,
  Box
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const regulatorTypes = [
  'Government Agency',
  'Industry Association',
  'Certification Body',
  'Auditor',
  'NGO',
  'Research Institution',
  'Other'
];

const jurisdictions = [
  'International',
  'Regional',
  'National',
  'State/Provincial',
  'Local',
  'Industry-Specific',
  'Other'
];

const RegulatorRegistrationForm = ({ onSubmit, loading }) => {
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      organizationName: '',
      role: '',
      regulatorType: '',
      jurisdiction: '',
      regulatoryFocus: ''
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Required'),
      lastName: Yup.string().required('Required'),
      email: Yup.string().email('Invalid email').required('Required'),
      password: Yup.string()
        .min(8, 'Must be at least 8 characters')
        .required('Required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Required'),
      organizationName: Yup.string().required('Required'),
      role: Yup.string().required('Required'),
      regulatorType: Yup.string().required('Required'),
      jurisdiction: Yup.string().required('Required')
    }),
    onSubmit: (values) => {
      // Add stakeholder type
      onSubmit({ 
        ...values, 
        stakeholderType: 'regulator',
        companyName: values.organizationName // Standardize field name for API
      });
    }
  });

  return (
    <form onSubmit={formik.handleSubmit} className="p-4">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="firstName"
            name="firstName"
            label="First Name"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="lastName"
            name="lastName"
            label="Last Name"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="organizationName"
            name="organizationName"
            label="Organization Name"
            value={formik.values.organizationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
            helperText={formik.touched.organizationName && formik.errors.organizationName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="role"
            name="role"
            label="Your Role in the Organization"
            value={formik.values.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role && formik.errors.role}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            error={formik.touched.regulatorType && Boolean(formik.errors.regulatorType)}
          >
            <InputLabel id="regulatorType-label">Regulator Type</InputLabel>
            <Select
              labelId="regulatorType-label"
              id="regulatorType"
              name="regulatorType"
              value={formik.values.regulatorType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {regulatorTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.regulatorType && formik.errors.regulatorType && (
              <FormHelperText>{formik.errors.regulatorType}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            error={formik.touched.jurisdiction && Boolean(formik.errors.jurisdiction)}
          >
            <InputLabel id="jurisdiction-label">Jurisdiction</InputLabel>
            <Select
              labelId="jurisdiction-label"
              id="jurisdiction"
              name="jurisdiction"
              value={formik.values.jurisdiction}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {jurisdictions.map((jurisdiction) => (
                <MenuItem key={jurisdiction} value={jurisdiction}>
                  {jurisdiction}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.jurisdiction && formik.errors.jurisdiction && (
              <FormHelperText>{formik.errors.jurisdiction}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="regulatoryFocus"
            name="regulatoryFocus"
            label="Regulatory Focus"
            multiline
            rows={3}
            value={formik.values.regulatoryFocus}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.regulatoryFocus && Boolean(formik.errors.regulatoryFocus)}
            helperText={formik.touched.regulatoryFocus && formik.errors.regulatoryFocus}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            className="gradient-bg"
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? 'Registering...' : 'Register as Regulator'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegulatorRegistrationForm;
